import { onMounted, onUnmounted, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'

/**
 * Composable for monitoring UI state consistency
 * Prevents menu items from disappearing due to auth state sync issues
 */
export function useUIStateMonitor() {
  const authStore = useAuthStore()
  let uiCheckInterval: NodeJS.Timeout | null = null
  let isChecking = ref(false)

  const checkUIState = async () => {
    if (isChecking.value) return
    isChecking.value = true

    try {
      // If we have user data but no session, try to restore
      if (authStore.user && !authStore.session) {
        console.log('🔍 UI State Monitor: User exists but no session, attempting restore...')
        const { data: { session: currentSession } } = await supabase.auth.getSession()
        if (currentSession) {
          console.log('✅ UI State Monitor: Found session, updating...')
          authStore.session = currentSession
        }
      }
      
      // If we have session but no user, try to restore
      if (authStore.session && !authStore.user) {
        console.log('🔍 UI State Monitor: Session exists but no user, attempting restore...')
        await authStore.restoreUserSession()
      }
    } catch (error) {
      console.error('UI State Monitor error:', error)
    } finally {
      isChecking.value = false
    }
  }

  const startUIStateMonitoring = () => {
    // Check UI state every 10 seconds to prevent menu disappearing (more frequent)
    uiCheckInterval = setInterval(checkUIState, 10000)
    
    // Also check immediately and then every 2 seconds for the first minute
    setTimeout(checkUIState, 1000)
    
    // More aggressive checking for the first minute
    const aggressiveInterval = setInterval(checkUIState, 2000)
    setTimeout(() => clearInterval(aggressiveInterval), 60000) // Stop after 1 minute
  }

  const stopUIStateMonitoring = () => {
    if (uiCheckInterval) {
      clearInterval(uiCheckInterval)
      uiCheckInterval = null
    }
  }

  onMounted(() => {
    startUIStateMonitoring()
  })

  onUnmounted(() => {
    stopUIStateMonitoring()
  })

  return {
    checkUIState,
    startUIStateMonitoring,
    stopUIStateMonitoring
  }
}
